import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Button, SelectDropDown, TabBar, TabItem, TextInput } from "@wot/components";
import { useState, useEffect, useRef } from "react";
import plansApi from "../../api/plans";
import { SPINNER } from "../../assets/animated";
import NoResults from "../../components/NoResults";
import Pagination from "../../components/Pagination";
import usePagainate from "../../hooks/usePaginate";
import { useTab } from "../../hooks/useTab";
import TableContainer from "./components/Table";
import { useClickOutside } from "@wot/shared";
import { FILTER_SVG } from "../../assets/icons";
import earningsApi from "../../api/earnings";

interface IFilters {
  status: string,
  modeOfPayment: string
}
export type ISorting = "createdAt"

export default function EarningsTable() {
  const queryClient = useQueryClient();
  const { activeTab, onTabChange } = useTab('all');
  const { currentPage, resultsPerPage, nextPage, prevPage, setResultsPerPage, resetPage } = usePagainate();
  const [sortBy, setSortBy] = useState<{ [key: string]: number }>({ "createdAt": -1 });
  const [filters, setFilters] = useState<IFilters | {}>({});

  useEffect(() => {
    resetPage();
  }, [activeTab]);

  const handleSort = (key: ISorting) => {
    setSortBy({ [key]: sortBy[key] === 1 ? -1 : 1 });
  };

  const { data, isLoading, isError, isPreviousData } = useQuery({
    queryKey: ['transactions', currentPage, resultsPerPage, activeTab, sortBy, filters],
    queryFn: async () =>
      await earningsApi.getTransactions(
        currentPage,
        resultsPerPage,
        { ...filters },
        { ...sortBy },
      )
  });

  useEffect(() => {
    if (!isPreviousData) {
      queryClient.prefetchQuery({
        queryKey: ['transactions', currentPage + 1],
        queryFn: async () =>
          await earningsApi.getTransactions(
            currentPage,
            resultsPerPage,
            { ...filters },
            { ...sortBy },
          )
      });
    }
  }, [data, isPreviousData, currentPage, resultsPerPage, queryClient, isError]);

  const filterResults = (filters: any) => {
    setFilters(filters);
  }

  if (isError) {
    return (
      <div className="w-full min-h-[256px] flex items-center justify-center">
        <p className="font-medium">Something went Wrong.</p>
      </div>
    );
  }


  return (
    <div className="w-full bg-white h-full rounded-xl py-6">

      <div className="w-1/2 flex border-b overflow-hidden no-scrollbar">
        <TabBar
          overflow="overflow-hidden"
          value={activeTab}
          indicatorColor="bg-primary"
          onChange={onTabChange}
          textColor="text-neutralGray1"
        >
          <></>
          {/* <TabItem label="All" value="all" fontSize={15} /> */}
          <TabItem label="Earnings  " value="earnings" />
          {/* <TabItem label="Expenses" value="expenses" /> */}
        </TabBar>
      </div>

      <Filters onApply={filterResults} onClear={() => setFilters({})} filters={filters} />
      {isLoading ? (
        <div className="w-full min-h-[256px] flex items-center justify-center">
          <img src={SPINNER} alt="loading" className="w-6 h-6" />
        </div>
      ) : data.results.length > 0 ? (
        <>
          <TableContainer handleSort={handleSort} data={data.results} />
          <Pagination
            count={data.count}
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={prevPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
          />
        </>
      ) : (
        <NoResults />
      )}
    </div>
  )
}



interface Props {
  onApply: (filters: any) => void
  onClear: () => void,
  filters: IFilters | {}
}
function Filters({ filters, onApply, onClear }: Props) {
  const [showOptions, setShowOptions] = useState(false);
  const filterSortRef = useRef(null);

  const [temp, setTemp] = useState<IFilters>({
    status: "",
    modeOfPayment: ""
  });

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      // @ts-ignore
      setTemp(filters);
    }
  }, [filters]);

  useClickOutside(filterSortRef, () => setShowOptions(false));

  return (
    <div className='relative flex justify-end'>
      <div onClick={() => setShowOptions(true)} className={`w-7 h-7 ${Object.keys(filters).length > 0 ? "bg-blue-100" : "bg-white"} flex items-center justify-center cursor-pointer border border-gray-300 rounded-md p-1 drop-shadow-sm`}>
        <img src={FILTER_SVG} />
      </div>
      {showOptions &&
        <div
          ref={filterSortRef}
          className="md:min-w-[500px] min-w-full flex flex-col border border-neutralBlue1 absolute top-14 md:right-5 right-0 p-6 bg-white drop-shadow-lg rounded-lg z-40"
        >
          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <p className="text-sm font-medium">Filter By Status</p>
              <div
                className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                onClick={() => setTemp({ ...temp, status: "" })}
              >
                Clear
              </div>
            </div>
            <div className="flex sm:flex-row flex-col gap-4 justify-between">
              <div
                className={`${temp.status === 'succeeded' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, status: "succeeded" })}
              >
                Succeeded
              </div>
              <div
                className={`${temp.status === 'failed' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, status: "failed" })}
              >
                Failed
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <p className="text-sm font-medium">Filter By Payment Mode</p>
              <div
                className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                onClick={() => setTemp({ ...temp, modeOfPayment: "" })}>
                Clear
              </div>
            </div>
            <div className="flex sm:flex-row flex-col gap-4 justify-between">
              <div
                className={`${temp.modeOfPayment === 'online' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, modeOfPayment: "online" })}>
                Online
              </div>
              <div
                className={`${temp.modeOfPayment === 'cash' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full  border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, modeOfPayment: "cash" })}>
                Cash
              </div>
            </div>
          </div>

          <div className="flex mt-5 gap-7">
            <Button
              text="Clear All"
              block
              type="s-outlined"
              onClick={() => {
                onClear();
                setShowOptions(false)
              }}
            />
            <Button
              text="Apply"
              block
              type="primary"
              onClick={() => {
                onApply(temp)
                setShowOptions(false);
              }}
            />
          </div>
        </div>
      }
    </div >
  )
}